/**
 * EntityFieldComponents - Shared sub-components for entity detail views
 *
 * Extracted from CoverageEntityDetails and MedicalProviderEntityDetails
 * to eliminate ~300 lines of duplicated rendering logic.
 *
 * Components:
 * - FieldStatusBadge: Shows "Will update" / "Read-only" badges
 * - FieldValueDisplay: Renders field values with type-aware formatting
 * - EditableFieldInput: Renders editable inputs (text, textarea, date, select)
 * - FieldRow: Complete row with label, status badge, and value/input
 *
 * Utilities:
 * - formatEnumValue: Converts SNAKE_CASE enum to "Title case"
 * - getSuggestedValue: Extracts a field's suggested value from a payload
 * - isFieldAffected: Checks if a field has a pending change
 */

'use client';

import { ArrowRight } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import type { FieldDefinition } from './types';

// ============================================================================
// Utility Functions
// ============================================================================

/** Format enum value for display: SNAKE_CASE → "Title case" */
export function formatEnumValue(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Get suggested value for a field from a payload object */
export function getSuggestedValue(
  payload: Record<string, unknown> | null | undefined,
  key: string,
): string | null | undefined {
  if (!payload) return undefined;
  if (key in payload) {
    const val = payload[key];
    return val === null ? null : val === undefined ? undefined : String(val);
  }
  return undefined;
}

/** Check if a field is affected by a change (case-insensitive) */
export function isFieldAffected(
  affectedSet: Set<string>,
  key: string,
): boolean {
  return affectedSet.has(key) || affectedSet.has(key.toLowerCase());
}

// ============================================================================
// Sub-components
// ============================================================================

/** Format a date string for display */
function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Status badge for field changes */
export const FieldStatusBadge: React.FC<{
  hasChange: boolean;
  isCreate: boolean;
  editable: boolean;
  canEdit: boolean;
}> = ({ hasChange, isCreate, editable, canEdit }) => {
  if (hasChange) {
    return (
      <Badge
        variant="outline"
        className="flex-shrink-0 border-amber-400 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0"
      >
        {isCreate ? 'Will create' : 'Will update'}
      </Badge>
    );
  }
  if (editable && !canEdit) {
    return (
      <Badge
        variant="outline"
        className="flex-shrink-0 border-zinc-300 bg-zinc-100 text-zinc-500 text-[10px] px-1.5 py-0"
      >
        Read-only
      </Badge>
    );
  }
  return null;
};

/** Render a field value for display */
export const FieldValueDisplay: React.FC<{
  value: string | null;
  fieldDef: FieldDefinition;
}> = ({ value, fieldDef }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-zinc-400">&mdash;</span>;
  }

  // Custom renderer (e.g., entity link components)
  if (fieldDef.renderCustomValue) {
    const custom = fieldDef.renderCustomValue(value);
    if (custom !== null) {
      return custom;
    }
  }

  if (fieldDef.type === 'date') {
    return <span className="text-zinc-900">{formatDate(value)}</span>;
  }

  if (fieldDef.type === 'select' && fieldDef.options) {
    const option = fieldDef.options.find((o) => o.value === value);
    return (
      <span className="text-zinc-900">
        {option?.label ?? formatEnumValue(value)}
      </span>
    );
  }

  return <span className="text-zinc-900">{value}</span>;
};

/** Editable field input */
export const EditableFieldInput: React.FC<{
  value: string | null;
  fieldDef: FieldDefinition;
  onChange: (value: string | null) => void;
}> = ({ value, fieldDef, onChange }) => {
  if (fieldDef.type === 'select' && fieldDef.options) {
    return (
      <Select value={value ?? undefined} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="h-8 text-sm">
          <SelectValue
            placeholder={`Select ${fieldDef.label.toLowerCase()}`}
          />
        </SelectTrigger>
        <SelectContent>
          {fieldDef.options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (fieldDef.type === 'textarea') {
    return (
      <Textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm min-h-[60px]"
        placeholder={`Enter ${fieldDef.label.toLowerCase()}`}
      />
    );
  }

  if (fieldDef.type === 'date') {
    return (
      <Input
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="h-8 text-sm"
      />
    );
  }

  return (
    <Input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-8 text-sm"
      placeholder={`Enter ${fieldDef.label.toLowerCase()}`}
    />
  );
};

/** Individual field row with label, status badge, and value/input */
export const FieldRow: React.FC<{
  fieldDef: FieldDefinition;
  currentValue: string | null;
  suggestedValue: string | null | undefined;
  hasChange: boolean;
  isCreate: boolean;
  editable: boolean;
  onValueChange?: (value: string | null) => void;
}> = ({
  fieldDef,
  currentValue,
  suggestedValue,
  hasChange,
  isCreate,
  editable,
  onValueChange,
}) => {
  const canEdit = editable && fieldDef.isEditable === true;
  const displayValue =
    suggestedValue !== undefined ? suggestedValue : currentValue;

  return (
    <div
      className={cn(
        'rounded-md px-3 py-2',
        hasChange ? 'ring-1 ring-amber-300 bg-amber-50' : 'bg-zinc-50',
      )}
    >
      {/* Label row */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-zinc-500">
          {fieldDef.label}
        </span>
        <FieldStatusBadge
          hasChange={hasChange}
          isCreate={isCreate}
          editable={editable}
          canEdit={canEdit}
        />
      </div>

      {/* Value content */}
      {canEdit && onValueChange ? (
        <EditableFieldInput
          value={displayValue}
          fieldDef={fieldDef}
          onChange={onValueChange}
        />
      ) : hasChange && !isCreate ? (
        <div className="flex items-center gap-2 text-sm flex-wrap">
          {currentValue != null && currentValue !== '' ? (
            <span className="text-zinc-500 line-through">
              <FieldValueDisplay value={currentValue} fieldDef={fieldDef} />
            </span>
          ) : (
            <span className="text-zinc-400 italic">(empty)</span>
          )}
          <ArrowRight className="h-3 w-3 text-amber-600 flex-shrink-0" />
          <span className="font-medium text-zinc-900">
            {suggestedValue === null || suggestedValue === '' ? (
              <span className="text-zinc-400 italic">(clear)</span>
            ) : (
              <FieldValueDisplay
                value={suggestedValue ?? null}
                fieldDef={fieldDef}
              />
            )}
          </span>
        </div>
      ) : hasChange && isCreate ? (
        <div className="text-sm">
          <span className="font-medium text-zinc-900">
            <FieldValueDisplay
              value={suggestedValue ?? null}
              fieldDef={fieldDef}
            />
          </span>
        </div>
      ) : (
        <div className="text-sm">
          <FieldValueDisplay value={currentValue} fieldDef={fieldDef} />
        </div>
      )}
    </div>
  );
};
